/**
 * Convict Labs Blog Module
 * Handles all interactive blog functionality for Shopify store
 *
 * Features:
 * - Table of Contents with scroll tracking
 * - FAQ Accordion with schema generation
 * - Sticky sidebar navigation
 * - Scroll progress indicator
 * - Read time calculator
 * - Category navigation
 * - Article search/filter
 * - Load more pagination
 * - Share buttons
 * - Lazy loading images
 * - Schema.org markup injection
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    wordsPerMinute: 200,
    debounceDelay: 300,
    stickyBreakpoint: 1024,
    faqSingleOpen: true,
    scrollProgressColor: '#FF6B35', // Convict Labs orange
    tocActiveClass: 'active',
    categoryActiveClass: 'active',
    fadeInDuration: 300,
  };

  // Utility Functions
  // ================

  /**
   * Debounce function to limit function calls
   */
  function debounce(func, delay) {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  /**
   * Slugify text for use as anchor IDs
   */
  function slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Get element offset relative to viewport
   */
  function getElementOffset(element) {
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      bottom: rect.bottom + window.scrollY,
    };
  }

  /**
   * Escape HTML for safe text insertion
   */
  function escapeHtml(unsafe) {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Format date to ISO string
   */
  function getISODate(date) {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  /**
   * IntersectionObserver wrapper for better browser support
   */
  function observeElements(selector, callback, options = {}) {
    if (!('IntersectionObserver' in window)) {
      console.warn('IntersectionObserver not supported');
      return null;
    }

    const defaultOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
      ...options,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        callback(entry);
      });
    }, defaultOptions);

    document.querySelectorAll(selector).forEach(el => {
      observer.observe(el);
    });

    return observer;
  }

  // 1. TABLE OF CONTENTS GENERATOR
  // ==============================

  const TableOfContents = {
    init: function(contentSelector = '#article-body', tocSelector = '#article-toc') {
      this.contentEl = document.querySelector(contentSelector);
      this.tocEl = document.querySelector(tocSelector);

      if (!this.contentEl || !this.tocEl) return;

      this.headings = this.extractHeadings();
      if (this.headings.length === 0) return;

      this.assignIds();
      this.buildTOC();
      this.initScrollTracking();
      this.initSmoothScroll();
      this.initMobileAccordion();
    },

    extractHeadings: function() {
      const headings = [];
      const h2Elements = this.contentEl.querySelectorAll('h2, h3');

      h2Elements.forEach((el) => {
        headings.push({
          element: el,
          level: parseInt(el.tagName[1]),
          text: el.textContent,
          id: null,
        });
      });

      return headings;
    },

    assignIds: function() {
      this.headings.forEach((heading) => {
        if (!heading.element.id) {
          heading.id = slugify(heading.text);
          heading.element.id = heading.id;
        } else {
          heading.id = heading.element.id;
        }
      });
    },

    buildTOC: function() {
      const isMobile = window.innerWidth < CONFIG.stickyBreakpoint;

      if (isMobile) {
        this.buildMobileAccordion();
      } else {
        this.buildDesktopTOC();
      }
    },

    buildDesktopTOC: function() {
      let html = '<nav class="toc-nav"><ol>';
      let currentLevel = 2;

      this.headings.forEach((heading) => {
        if (heading.level === 2) {
          if (currentLevel > 2) {
            html += '</ol></li>';
          }
          html += `<li><a href="#${heading.id}" class="toc-link" data-id="${heading.id}">${escapeHtml(heading.text)}</a>`;
          currentLevel = 2;
        } else if (heading.level === 3 && currentLevel === 2) {
          html += '<ol>';
          html += `<li><a href="#${heading.id}" class="toc-link" data-id="${heading.id}">${escapeHtml(heading.text)}</a></li>`;
          currentLevel = 3;
        } else if (heading.level === 3) {
          html += `<li><a href="#${heading.id}" class="toc-link" data-id="${heading.id}">${escapeHtml(heading.text)}</a></li>`;
        }
      });

      if (currentLevel > 2) {
        html += '</ol></li>';
      }
      html += '</ol></nav>';

      this.tocEl.innerHTML = html;
    },

    buildMobileAccordion: function() {
      const html = `
        <div class="toc-mobile-accordion">
          <button class="toc-toggle" aria-expanded="false">
            <span>Contents</span>
            <svg class="toc-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7 8l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <nav class="toc-nav-mobile" style="display: none;">
            <ol>
              ${this.headings.map(h => `
                <li style="padding-left: ${(h.level - 2) * 16}px">
                  <a href="#${h.id}" class="toc-link" data-id="${h.id}">${escapeHtml(h.text)}</a>
                </li>
              `).join('')}
            </ol>
          </nav>
        </div>
      `;

      this.tocEl.innerHTML = html;

      const toggle = this.tocEl.querySelector('.toc-toggle');
      const nav = this.tocEl.querySelector('.toc-nav-mobile');

      toggle.addEventListener('click', () => {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', !isExpanded);
        nav.style.display = isExpanded ? 'none' : 'block';
      });
    },

    initScrollTracking: function() {
      if (!('IntersectionObserver' in window)) return;

      const headingSelectors = this.headings.map(h => `#${h.id}`).join(', ');
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.setActiveLink(entry.target.id);
            }
          });
        },
        { rootMargin: '-50% 0px -50% 0px' }
      );

      this.headings.forEach(h => observer.observe(h.element));
      this.scrollObserver = observer;
    },

    setActiveLink: function(id) {
      const links = this.tocEl.querySelectorAll('.toc-link');
      links.forEach(link => {
        link.classList.toggle(CONFIG.tocActiveClass, link.dataset.id === id);
      });
    },

    initSmoothScroll: function() {
      const links = this.tocEl.querySelectorAll('.toc-link');
      links.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const id = link.dataset.id;
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });
    },

    initMobileAccordion: function() {
      window.addEventListener('resize', debounce(() => {
        const isMobile = window.innerWidth < CONFIG.stickyBreakpoint;
        if (isMobile && !this.tocEl.querySelector('.toc-mobile-accordion')) {
          this.buildTOC();
          this.initScrollTracking();
          this.initSmoothScroll();
        }
      }, 250));
    },

    destroy: function() {
      if (this.scrollObserver) {
        this.scrollObserver.disconnect();
      }
    },
  };

  // 2. FAQ ACCORDION
  // ================

  const FAQAccordion = {
    init: function(itemSelector = '.faq-item') {
      this.items = document.querySelectorAll(itemSelector);
      if (this.items.length === 0) return;

      this.items.forEach((item, index) => {
        this.setupItem(item, index);
      });

      this.generateFAQSchema();
    },

    setupItem: function(item, index) {
      const toggle = item.querySelector('h3, .faq-question');
      const answer = item.querySelector('.faq-answer, [class*="answer"]');

      if (!toggle || !answer) return;

      // Add unique ID if missing
      if (!item.id) {
        item.id = `faq-item-${index}`;
      }

      toggle.setAttribute('role', 'button');
      toggle.setAttribute('tabindex', '0');
      toggle.setAttribute('aria-expanded', 'false');

      const icon = document.createElement('span');
      icon.className = 'faq-icon';
      icon.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M7 8l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      toggle.appendChild(icon);

      // Store initial height
      answer.style.maxHeight = '0';
      answer.style.overflow = 'hidden';
      answer.style.transition = 'max-height 0.3s ease-out';

      const toggleHandler = () => {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        this.toggleItem(item, toggle, answer, !isOpen);
      };

      toggle.addEventListener('click', toggleHandler);
      toggle.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleHandler();
        }
      });
    },

    toggleItem: function(item, toggle, answer, open) {
      const icon = toggle.querySelector('.faq-icon svg');

      if (open) {
        toggle.setAttribute('aria-expanded', 'true');
        answer.style.maxHeight = answer.scrollHeight + 'px';
        item.classList.add('open');
        if (icon) icon.style.transform = 'rotate(180deg)';

        // Close other items if configured
        if (CONFIG.faqSingleOpen) {
          this.items.forEach((otherItem) => {
            if (otherItem !== item) {
              const otherToggle = otherItem.querySelector('h3, .faq-question');
              const otherAnswer = otherItem.querySelector('.faq-answer, [class*="answer"]');
              if (otherToggle && otherToggle.getAttribute('aria-expanded') === 'true') {
                this.toggleItem(otherItem, otherToggle, otherAnswer, false);
              }
            }
          });
        }
      } else {
        toggle.setAttribute('aria-expanded', 'false');
        answer.style.maxHeight = '0';
        item.classList.remove('open');
        if (icon) icon.style.transform = 'rotate(0deg)';
      }
    },

    generateFAQSchema: function() {
      const faqs = [];

      this.items.forEach((item) => {
        const question = item.querySelector('h3, .faq-question');
        const answer = item.querySelector('.faq-answer, [class*="answer"]');

        if (question && answer) {
          faqs.push({
            '@type': 'Question',
            name: question.textContent.trim(),
            acceptedAnswer: {
              '@type': 'Answer',
              text: answer.textContent.trim(),
            },
          });
        }
      });

      if (faqs.length > 0) {
        this.injectSchema({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs,
        });
      }
    },

    injectSchema: function(schema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    },
  };

  // 3. STICKY SIDEBAR
  // =================

  const StickySidebar = {
    init: function(sidebarSelector = '.article-sidebar', articleSelector = '.article-content') {
      this.sidebar = document.querySelector(sidebarSelector);
      this.article = document.querySelector(articleSelector);

      if (!this.sidebar || !this.article) return;

      this.setupIntersectionObserver();
      this.handleResize();

      window.addEventListener('resize', debounce(() => this.handleResize(), 250));
    },

    setupIntersectionObserver: function() {
      if (!('IntersectionObserver' in window)) return;

      const topSentinel = document.createElement('div');
      topSentinel.className = 'sidebar-sentinel-top';
      this.article.insertBefore(topSentinel, this.article.firstChild);

      const bottomSentinel = document.createElement('div');
      bottomSentinel.className = 'sidebar-sentinel-bottom';
      this.article.appendChild(bottomSentinel);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target === topSentinel) {
              this.updateSticky(!entry.isIntersecting);
            }
            if (entry.target === bottomSentinel) {
              this.updateSticky(entry.isIntersecting);
            }
          });
        },
        { threshold: 0 }
      );

      observer.observe(topSentinel);
      observer.observe(bottomSentinel);
      this.observer = observer;
    },

    updateSticky: function(shouldStick) {
      if (window.innerWidth < CONFIG.stickyBreakpoint) {
        this.sidebar.classList.remove('sticky');
        return;
      }

      if (shouldStick) {
        this.sidebar.classList.add('sticky');
      } else {
        this.sidebar.classList.remove('sticky');
      }
    },

    handleResize: function() {
      if (window.innerWidth < CONFIG.stickyBreakpoint) {
        this.sidebar.classList.remove('sticky');
      }
    },
  };

  // 4. SCROLL PROGRESS INDICATOR
  // ============================

  const ScrollProgress = {
    init: function(contentSelector = '.article-content') {
      this.content = document.querySelector(contentSelector);
      if (!this.content) return;

      this.createProgressBar();
      this.setupScrollTracking();
    },

    createProgressBar: function() {
      const bar = document.createElement('div');
      bar.className = 'scroll-progress-bar';
      bar.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        height: 3px;
        background-color: ${CONFIG.scrollProgressColor};
        z-index: 9999;
        width: 0;
        transition: width 0.1s ease-out;
      `;
      document.body.appendChild(bar);
      this.progressBar = bar;
    },

    setupScrollTracking: function() {
      const updateProgress = () => {
        const contentRect = this.content.getBoundingClientRect();
        const contentHeight = this.content.offsetHeight;
        const windowHeight = window.innerHeight;

        // Calculate scroll progress relative to content only
        const scrollStart = contentRect.top;
        const scrollEnd = contentRect.bottom - windowHeight;
        const totalScroll = contentHeight - windowHeight;

        let progress = 0;
        if (scrollStart <= 0) {
          progress = Math.min(100, Math.max(0, (-scrollStart / totalScroll) * 100));
        }

        this.progressBar.style.width = progress + '%';
        this.animationFrame = requestAnimationFrame(updateProgress);
      };

      window.addEventListener('scroll', updateProgress, { passive: true });
      updateProgress();
    },
  };

  // 5. READ TIME CALCULATOR
  // =======================

  const ReadTimeCalculator = {
    init: function(contentSelector = '.article-content') {
      const content = document.querySelector(contentSelector);
      if (!content) return;

      const readTime = this.calculateReadTime(content.textContent);
      this.updateReadTimeElements(readTime);
    },

    calculateReadTime: function(text) {
      const words = text.trim().split(/\s+/).length;
      const minutes = Math.ceil(words / CONFIG.wordsPerMinute);
      return Math.max(1, minutes); // Minimum 1 minute
    },

    updateReadTimeElements: function(minutes) {
      const elements = document.querySelectorAll('.read-time');
      elements.forEach((el) => {
        el.textContent = `${minutes} min read`;
        el.setAttribute('aria-label', `Reading time: ${minutes} minutes`);
      });
    },
  };

  // 6. CATEGORY NAVIGATION
  // ======================

  const CategoryNavigation = {
    init: function(navSelector = '.category-nav', activeSelector = '[data-category]') {
      this.nav = document.querySelector(navSelector);
      if (!this.nav) return;

      this.setupScrollButtons();
      this.setActivePill();
      this.initSmoothScroll();

      window.addEventListener('resize', debounce(() => this.checkOverflow(), 250));
      this.checkOverflow();
    },

    setupScrollButtons: function() {
      const innerContainer = this.nav.querySelector('.category-items, .category-nav__list, .category-nav__container');
      const container = innerContainer || this.nav;

      if (!this.nav.querySelector('.category-scroll-btn') && innerContainer) {
        const prevBtn = document.createElement('button');
        prevBtn.className = 'category-scroll-btn category-scroll-prev';
        prevBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20"><path d="M12 4l-6 6 6 6" stroke="currentColor" stroke-width="2"/></svg>';
        prevBtn.addEventListener('click', () => this.scroll(-200));

        const nextBtn = document.createElement('button');
        nextBtn.className = 'category-scroll-btn category-scroll-next';
        nextBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20"><path d="M8 4l6 6-6 6" stroke="currentColor" stroke-width="2"/></svg>';
        nextBtn.addEventListener('click', () => this.scroll(200));

        this.nav.insertBefore(prevBtn, innerContainer);
        this.nav.appendChild(nextBtn);
      }

      this.container = container;
    },

    scroll: function(distance) {
      this.container.scrollBy({ left: distance, behavior: 'smooth' });
    },

    checkOverflow: function() {
      if (!this.container) return;
      const isOverflowing = this.container.scrollWidth > this.container.clientWidth;
      const btns = this.nav.querySelectorAll('.category-scroll-btn');
      btns.forEach(btn => btn.style.display = isOverflowing && window.innerWidth >= CONFIG.stickyBreakpoint ? 'flex' : 'none');
    },

    setActivePill: function() {
      const currentTag = document.body.getAttribute('data-current-tag') ||
                         new URLSearchParams(window.location.search).get('tag') ||
                         'all';

      const pills = this.nav.querySelectorAll('[data-category], .category-pill');
      pills.forEach((pill) => {
        const tag = pill.getAttribute('data-category') || pill.textContent.toLowerCase();
        pill.classList.toggle(CONFIG.categoryActiveClass, tag === currentTag || tag.includes(currentTag));
      });
    },

    initSmoothScroll: function() {
      const pills = this.nav.querySelectorAll('[data-category], .category-pill');
      pills.forEach((pill) => {
        pill.addEventListener('click', (e) => {
          e.preventDefault();
          const activeClass = CONFIG.categoryActiveClass;
          pills.forEach(p => p.classList.remove(activeClass));
          pill.classList.add(activeClass);
        });
      });
    },
  };

  // 7. ARTICLE SEARCH
  // =================

  const ArticleSearch = {
    init: function(searchSelector = '#blog-search', cardSelector = '.article-card') {
      this.searchInput = document.querySelector(searchSelector);
      this.cards = document.querySelectorAll(cardSelector);

      if (!this.searchInput || this.cards.length === 0) return;

      this.setupSearch();
      this.createNoResultsMessage();
    },

    setupSearch: function() {
      const debouncedSearch = debounce(() => this.filterArticles(), CONFIG.debounceDelay);
      this.searchInput.addEventListener('keyup', debouncedSearch);
      this.searchInput.addEventListener('input', debouncedSearch);
    },

    filterArticles: function() {
      const query = this.searchInput.value.toLowerCase().trim();
      let visibleCount = 0;

      this.cards.forEach((card) => {
        const title = (card.querySelector('[data-title]')?.textContent ||
                      card.querySelector('h3')?.textContent || '').toLowerCase();
        const excerpt = (card.querySelector('[data-excerpt]')?.textContent ||
                        card.querySelector('p')?.textContent || '').toLowerCase();

        const matches = title.includes(query) || excerpt.includes(query) || query === '';

        if (matches) {
          card.style.display = '';
          card.style.opacity = '1';
          visibleCount++;
        } else {
          card.style.display = 'none';
          card.style.opacity = '0';
        }
      });

      this.updateNoResults(visibleCount === 0 && query !== '');
    },

    createNoResultsMessage: function() {
      const container = this.searchInput.closest('.search-container') || this.searchInput.parentElement;
      const message = document.createElement('div');
      message.className = 'search-no-results';
      message.style.display = 'none';
      message.style.cssText += 'text-align: center; padding: 40px 20px; color: #999;';
      message.textContent = 'No articles found matching your search.';
      container.parentElement.insertBefore(message, container.nextElementSibling);
      this.noResultsMessage = message;
    },

    updateNoResults: function(show) {
      if (this.noResultsMessage) {
        this.noResultsMessage.style.display = show ? 'block' : 'none';
      }
    },
  };

  // 8. LOAD MORE PAGINATION
  // =======================

  const LoadMorePagination = {
    init: function(buttonSelector = '.blog-load-more-btn', gridSelector = '.blog-grid') {
      this.button = document.querySelector(buttonSelector);
      this.grid = document.querySelector(gridSelector);

      if (!this.button || !this.grid) return;

      this.page = 2;
      this.isLoading = false;
      this.button.addEventListener('click', () => this.loadMore());
    },

    loadMore: function() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.button.disabled = true;
      const originalText = this.button.textContent;
      this.button.textContent = 'Loading...';

      const blogPath = window.location.pathname;
      const url = `${blogPath}?page=${this.page}`;

      fetch(url)
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newCards = doc.querySelectorAll('.article-card');

          if (newCards.length === 0) {
            this.button.style.display = 'none';
          } else {
            this.appendCards(newCards);
            this.page++;
            this.button.disabled = false;
            this.button.textContent = originalText;
          }

          this.isLoading = false;
        })
        .catch(error => {
          console.error('Error loading more articles:', error);
          this.button.disabled = false;
          this.button.textContent = originalText;
          this.isLoading = false;
        });
    },

    appendCards: function(cards) {
      cards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.style.opacity = '0';
        this.grid.appendChild(clone);

        // Trigger fade-in animation
        requestAnimationFrame(() => {
          clone.style.transition = `opacity ${CONFIG.fadeInDuration}ms ease-in`;
          clone.style.opacity = '1';
        });
      });
    },
  };

  // 9. SHARE BUTTONS
  // ================

  const ShareButtons = {
    init: function(shareButtonSelector = '.share-button') {
      this.shareButtons = document.querySelectorAll(shareButtonSelector);
      if (this.shareButtons.length === 0) return;

      this.setupShareButtons();
    },

    setupShareButtons: function() {
      this.shareButtons.forEach((btn) => {
        const platform = btn.getAttribute('data-platform') || btn.className.match(/share-(\w+)/)?.[1];

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleShare(platform, btn);
        });
      });
    },

    handleShare: function(platform, btn) {
      const url = window.location.href;
      const title = document.querySelector('h1')?.textContent || document.title;
      const text = document.querySelector('meta[property="og:description"]')?.content || '';

      switch (platform) {
        case 'copy':
          this.copyToClipboard(url, btn);
          break;
        case 'twitter':
          this.openShare(`https://x.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`);
          break;
        case 'facebook':
          this.openShare(`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
          break;
        case 'linkedin':
          this.openShare(`https://linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);
          break;
        case 'native':
          this.nativeShare(title, text, url);
          break;
      }
    },

    copyToClipboard: function(text, btn) {
      navigator.clipboard.writeText(text).then(() => {
        const originalText = btn.textContent;
        btn.textContent = 'Copied!';
        btn.setAttribute('aria-label', 'Link copied to clipboard');
        setTimeout(() => {
          btn.textContent = originalText;
        }, 2000);
      }).catch(() => {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      });
    },

    openShare: function(url) {
      window.open(url, 'share', 'width=600,height=400');
    },

    nativeShare: function(title, text, url) {
      if (navigator.share) {
        navigator.share({
          title,
          text,
          url,
        }).catch(() => {
          // User cancelled share
        });
      } else {
        // Fallback to copy
        const btn = document.querySelector('.share-btn[data-platform="native"]');
        if (btn) this.copyToClipboard(url, btn);
      }
    },
  };

  // 10. LAZY LOADING IMAGES
  // =======================

  const LazyLoadImages = {
    init: function(imageSelector = 'img[loading="lazy"]') {
      this.images = document.querySelectorAll(imageSelector);
      if (this.images.length === 0) return;

      // Native support check
      if ('loading' in HTMLImageElement.prototype) {
        // Browser supports native lazy loading
        return;
      }

      // Fallback: IntersectionObserver
      this.setupIntersectionObserver();
    },

    setupIntersectionObserver: function() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: load all images
        this.images.forEach(img => this.loadImage(img));
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadImage(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '50px',
      });

      this.images.forEach(img => observer.observe(img));
    },

    loadImage: function(img) {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
      img.addEventListener('load', () => img.classList.add('loaded'));
    },
  };

  // 11. SCHEMA INJECTION
  // ====================

  const SchemaInjection = {
    init: function() {
      this.injectArticleSchema();
      this.injectBreadcrumbSchema();
    },

    injectArticleSchema: function() {
      const article = document.querySelector('[data-schema-article]');
      if (!article) return;

      const schema = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: article.getAttribute('data-title') || document.querySelector('h1')?.textContent,
        description: article.getAttribute('data-excerpt') ||
                    document.querySelector('meta[name="description"]')?.content,
        image: article.getAttribute('data-image') ||
               document.querySelector('meta[property="og:image"]')?.content,
        datePublished: article.getAttribute('data-published') ||
                      document.querySelector('meta[property="article:published_time"]')?.content,
        dateModified: article.getAttribute('data-modified') ||
                     document.querySelector('meta[property="article:modified_time"]')?.content,
        author: {
          '@type': 'Person',
          name: article.getAttribute('data-author') || 'Convict Labs',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Convict Labs',
          logo: {
            '@type': 'ImageObject',
            url: article.getAttribute('data-publisher-logo') || '/logo.png',
          },
        },
      };

      this.injectSchema(schema);
    },

    injectBreadcrumbSchema: function() {
      const breadcrumbs = document.querySelector('.breadcrumb');
      if (!breadcrumbs) return;

      const items = breadcrumbs.querySelectorAll('a, [data-breadcrumb]');
      if (items.length === 0) return;

      const breadcrumbList = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: Array.from(items).map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.textContent.trim(),
          item: item.href || item.getAttribute('data-url') || window.location.href,
        })),
      };

      this.injectSchema(breadcrumbList);
    },

    injectSchema: function(schema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    },
  };

  // Initialize all modules on DOMContentLoaded
  // ==========================================

  function safeInit(name, fn) {
    try {
      fn();
    } catch (e) {
      console.warn('[ConvictLabsBlog] Error initializing ' + name + ':', e.message);
    }
  }

  function initializeAll() {
    // Detect if we're on a blog article or blog listing
    const isArticle = document.querySelector('.article-content, #article-body, .convict-article-section');
    const isBlogListing = document.querySelector('.blog-grid, .convict-blog-section');

    if (isArticle) {
      // Article page features
      safeInit('TableOfContents', () => TableOfContents.init());
      safeInit('FAQAccordion', () => FAQAccordion.init());
      safeInit('StickySidebar', () => StickySidebar.init());
      safeInit('ScrollProgress', () => ScrollProgress.init());
      safeInit('ReadTimeCalculator', () => ReadTimeCalculator.init());
      safeInit('LazyLoadImages', () => LazyLoadImages.init());
      safeInit('ShareButtons', () => ShareButtons.init());
      safeInit('SchemaInjection', () => SchemaInjection.init());
    }

    if (isBlogListing) {
      // Blog listing page features
      safeInit('CategoryNavigation', () => CategoryNavigation.init());
      safeInit('ArticleSearch', () => ArticleSearch.init());
      safeInit('LoadMorePagination', () => LoadMorePagination.init());
      safeInit('LazyLoadImages', () => LazyLoadImages.init());
      safeInit('SchemaInjection', () => SchemaInjection.init());
    }

    // Global features
    if (!isBlogListing) {
      safeInit('CategoryNavigation', () => CategoryNavigation.init());
    }
  }

  // Ensure DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll);
  } else {
    initializeAll();
  }

  // Export for testing (if used in module context)
  window.ConvictLabsBlog = {
    TableOfContents,
    FAQAccordion,
    StickySidebar,
    ScrollProgress,
    ReadTimeCalculator,
    CategoryNavigation,
    ArticleSearch,
    LoadMorePagination,
    ShareButtons,
    LazyLoadImages,
    SchemaInjection,
  };
})();
